import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL as API_URL } from '../apiBase';
import { clearSessionStarted, markSessionStarted } from '../lib/sessionTimer';
import { resetAuraxFlags } from '../lib/auraxFlags'; // EXTRAS_FIX_2910

axios.defaults.withCredentials = true;
if (!axios.defaults.timeout) axios.defaults.timeout = 25000;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const formatApiError = (detail) => {
  if (detail == null) return '';

  const translateMsg = (msg) => {
    const m = String(msg || '').trim();
    if (!m) return '';
    const lower = m.toLowerCase();
    if (
      lower === 'internal server error'
      || lower === 'erro interno do servidor'
      || lower.includes('500 internal server error')
    ) {
      return 'O servidor encontrou uma falha inesperada. Tente novamente e, se persistir, informe o horário à equipe técnica.';
    }
    if (
      lower.includes('cloudflare')
      || lower.includes('invalid or incomplete response')
      || lower.includes('origin web server')
    ) {
      return 'Falha ao contactar o servidor. Tente novamente; se persistir, confira o WhatsApp ligado e o número da revenda.';
    }
    if (
      lower.includes('valid integer') ||
      lower.includes('unable to parse string as an integer') ||
      lower.includes('input should be a valid integer')
    ) {
      return 'Identificador inválido. Atualize a página e tente novamente.';
    }
    if (lower.includes('field required') || lower.includes('field required')) {
      return 'Campo obrigatório em falta.';
    }
    if (lower.includes('value is not a valid email')) {
      return 'E-mail inválido.';
    }
    if (lower.includes('json decode error') || lower.includes('json_invalid')) {
      return 'Pedido inválido. Tente novamente.';
    }
    return m;
  };

  if (typeof detail === 'string') return translateMsg(detail);
  if (Array.isArray(detail)) {
    const parts = detail
      .map((e) => {
        if (!e) return '';
        if (typeof e === 'string') return translateMsg(e);
        if (typeof e.msg === 'string') return translateMsg(e.msg);
        return '';
      })
      .filter(Boolean);
    // Evita repetir a mesma mensagem N vezes (ex.: 3 IDs inválidos)
    return [...new Set(parts)].join(' ');
  }
  if (detail && typeof detail.message === 'string') return translateMsg(detail.message);
  if (detail && typeof detail.msg === 'string') return translateMsg(detail.msg);
  return translateMsg(String(detail));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountSummary, setAccountSummary] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      delete axios.defaults.headers.common['Authorization'];
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      const me = {
        ...data,
        role: typeof data.role === 'string' ? data.role : data.role?.value || data.role,
      };
      setUser(me);
      // Libera a UI assim que /me responde; summary completa em seguida sem bloquear.
      setLoading(false);
      if (me.password_change_required) { setAccountSummary(null); return; }
      try {
        const { data: summary } = await axios.get(`${API_URL}/api/account/summary`, { withCredentials: true });
        setAccountSummary(summary || null);
      } catch (e) {
        setAccountSummary(null);
      }
    } catch (error) {
      console.error("Erro na autenticação:", error);
      setUser(false);
      setAccountSummary(null);
      setLoading(false);
    }
  }, []);

  const refreshAccountSummary = useCallback(async () => {
    if (!user || user.password_change_required) {
      setAccountSummary(null);
      return;
    }
    try {
      const { data: summary } = await axios.get(`${API_URL}/api/account/summary`, { withCredentials: true });
      setAccountSummary(summary || null);
    } catch {
      setAccountSummary(null);
    }
  }, [user]);

  const updateProfile = useCallback(async (changes) => {
    const { data } = await axios.put(`${API_URL}/api/auth/me`, changes, { withCredentials: true });
    setUser((current) => current?.id === data.id ? { ...current, ...data } : current);
    return data;
  }, []);

  const applyAvatarSnapshot = useCallback((accountId, snapshot) => {
    if (!accountId || !snapshot || typeof snapshot.has_avatar !== 'boolean') return;
    const avatar = snapshot.has_avatar && typeof snapshot.avatar_url === 'string' ? snapshot.avatar_url : null;
    if (snapshot.has_avatar && !avatar) return;
    // A late upload response must never update a different signed-in account.
    setUser((current) => current?.id === accountId
      ? { ...current, has_avatar: snapshot.has_avatar, avatar_url: avatar } : current);
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use((r) => r, (error) => {
      if (error.response?.data?.detail?.code === 'password_change_required') {
        setUser((current) => current ? { ...current, password_change_required: true } : current);
        setAccountSummary(null);
      }
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem('token');
    } catch (_) {
      /* ignore */
    }
    checkAuth();
  }, [checkAuth]);

  // Reflect per-attendant grants without forcing a logout or refreshing the page.
  // The server always rechecks the current policy on each write, including revocations.
  useEffect(() => {
    if (user?.role !== 'attendant' || !user?.id) return undefined;
    let cancelled = false;
    let pending = false;
    const refreshOperations = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        if (!cancelled && data?.id === user.id && data?.role === 'attendant') {
          // Grants and their owner scope are one snapshot. Login responses are
          // intentionally smaller than /me; refreshing only grants left valid
          // reseller actions disabled because root_admin_id was still missing.
          const access = {
            operational_permissions: data.operational_permissions,
            root_admin_id: data.root_admin_id,
            managed_by_reseller_id: data.managed_by_reseller_id,
          };
          setUser((current) => current?.id !== data.id || current?.role !== 'attendant' ||
            Object.keys(access).every((key) => JSON.stringify(current[key]) === JSON.stringify(access[key]))
            ? current : { ...current, ...access });
        }
      } catch { /* A transient failure must not reset the session or infer new grants. */ }
      finally { pending = false; }
    };
    const timer = window.setInterval(refreshOperations, 30000);
    window.addEventListener('focus', refreshOperations);
    document.addEventListener('visibilitychange', refreshOperations);
    return () => {
      cancelled = true; window.clearInterval(timer);
      window.removeEventListener('focus', refreshOperations);
      document.removeEventListener('visibilitychange', refreshOperations);
    };
  }, [user?.id, user?.role]);

  const applySessionUser = async (data) => {
    setTimeout(() => { try { resetAuraxFlags(); } catch (_) { /* noop */ } }, 0); // EXTRAS_FIX_2910
    delete axios.defaults.headers.common['Authorization'];
    try {
      localStorage.removeItem('token');
    } catch (_) {
      /* ignore */
    }
    let normalized = {
      ...data,
      role: typeof data.role === 'string' ? data.role : data.role?.value || data.role,
    };
    let sessionMismatch = false;
    // Login responses are deliberately smaller than /me. Hydrate the complete
    // session before mounting the shell, including the versioned profile photo.
    try {
      const { data: me } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      const role = typeof me?.role === 'string' ? me.role : me?.role?.value;
      if (!me?.id || me.id !== normalized.id || role !== normalized.role) { sessionMismatch = true; throw new Error('Session mismatch'); }
      normalized = { ...me, role };
    } catch {
      if (normalized.role === 'attendant' || sessionMismatch) {
        // Never infer attendant grants from the smaller login response.
        setUser(false);
        setAccountSummary(null);
        throw new Error('Não foi possível carregar as permissões da sua conta. Atualize a página ou tente entrar novamente.');
      }
    }
    setUser(normalized);
    markSessionStarted({ force: true });
    if (normalized.password_change_required) { setAccountSummary(null); return normalized; }
    try {
      const { data: summary } = await axios.get(`${API_URL}/api/account/summary`, { withCredentials: true });
      setAccountSummary(summary || null);
    } catch (e) {
      setAccountSummary(null);
    }
    return normalized;
  };

  const login = async (email, password, turnstileToken) => {
    const payload = {
      email: String(email || '').trim(),
      password: String(password || '').replace(/\u00a0/g, ' ').trim(),
    };
    if (turnstileToken) payload.turnstile_token = turnstileToken;
    const res = await axios.post(`${API_URL}/api/auth/login`, payload, { withCredentials: true });
    const data = res && res.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      const e = new Error('Servidor indisponível. Aguarde alguns segundos e tente novamente.');
      e.isHtmlResponse = true;
      throw e;
    }

    if (data.mfa_required) {
      return data;
    }

    return applySessionUser(data);
  };

  const loginWithGoogle = async (credential) => {
    const res = await axios.post(
      `${API_URL}/api/auth/google`,
      { credential },
      { withCredentials: true },
    );
    const data = res && res.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      const e = new Error('Servidor indisponível. Aguarde alguns segundos e tente novamente.');
      e.isHtmlResponse = true;
      throw e;
    }
    if (data.mfa_required) {
      return data;
    }
    return applySessionUser(data);
  };

  const registerReferralWithGoogle = async (code, payload) => {
    const res = await axios.post(
      `${API_URL}/api/auth/register-ref/${encodeURIComponent(code)}/google`,
      payload,
      { withCredentials: true },
    );
    const data = res && res.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      const e = new Error('Servidor indisponível. Aguarde alguns segundos e tente novamente.');
      e.isHtmlResponse = true;
      throw e;
    }
    try {
      await checkAuth();
    } catch {
      /* sessão confirmada ao entrar */
    }
    return data;
  };

  const verifyMfa = async (code) => {
    const res = await axios.post(
      `${API_URL}/api/auth/mfa/verify`,
      { code },
      { withCredentials: true },
    );
    const data = res && res.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      const e = new Error('Servidor indisponível. Aguarde alguns segundos e tente novamente.');
      e.isHtmlResponse = true;
      throw e;
    }
    return applySessionUser(data);
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(
      `${API_URL}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );

    delete axios.defaults.headers.common['Authorization'];
    try {
      localStorage.removeItem('token');
    } catch (_) {
      /* ignore */
    }

    setUser(data);
    markSessionStarted({ force: true });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    try {
      localStorage.removeItem('token');
    } catch (_) {
      /* ignore */
    }
    clearSessionStarted();
    delete axios.defaults.headers.common['Authorization'];
    setUser(false);
    setAccountSummary(null);
  };

  const impersonateUser = async (userId) => {
    const res = await axios.post(
      `${API_URL}/api/auth/impersonate`,
      { user_id: String(userId || '').trim() },
      { withCredentials: true },
    );
    const data = res && res.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      const e = new Error('Servidor indisponível. Aguarde alguns segundos e tente novamente.');
      e.isHtmlResponse = true;
      throw e;
    }
    return applySessionUser({ ...data, impersonating: true });
  };

  const stopImpersonation = async () => {
    const res = await axios.post(
      `${API_URL}/api/auth/stop-impersonation`,
      {},
      { withCredentials: true },
    );
    const data = res && res.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      const e = new Error('Servidor indisponível. Aguarde alguns segundos e tente novamente.');
      e.isHtmlResponse = true;
      throw e;
    }
    return applySessionUser({ ...data, impersonating: false });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithGoogle,
        registerReferralWithGoogle,
        verifyMfa,
        register,
        logout,
        impersonateUser,
        stopImpersonation,
        checkAuth,
        applyAvatarSnapshot,
        refreshAccountSummary,
        updateProfile,
        accountSummary,
        isAdmin: user?.role === 'admin',
        isReseller: user?.role === 'reseller',
        isAttendant: user?.role === 'attendant',
        isImpersonating: Boolean(user?.impersonating),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
